import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import Reveal from '../components/Reveal';
import { PillButton } from '../components/Pill';
import { blob, spin } from '../animations';
import { siteConfig } from '../siteConfig';
import { trackEvent } from '../analytics';

const FORMSPREE_ENDPOINT = 'https://formspree.io/f/xrbpnqaz';
const STATUS_TIMEOUT_MS = 5000;

const Section = styled.section`
  position: relative;
  overflow: hidden;
  padding: clamp(90px, 14vh, 180px) ${({ theme }) => theme.container.gutter};
  background: ${({ theme }) => theme.colors.ink};
  color: ${({ theme }) => theme.colors.paper};
`;

const Blob = styled.div`
  position: absolute;
  bottom: -14%;
  left: -6%;
  width: 40vw;
  height: 40vw;
  background: radial-gradient(circle, ${({ theme }) => theme.colors.accent}, transparent 66%);
  filter: blur(50px);
  opacity: 0.5;
  ${blob(18, 29)};
`;

const Inner = styled.div`
  position: relative;
  z-index: 2;
  max-width: 1100px;
  margin: 0 auto;
  text-align: center;
`;

const Kicker = styled(Reveal)`
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 0.78rem;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.sky};
  margin-bottom: 26px;
`;

const Title = styled(Reveal)`
  font-size: clamp(2.6rem, 8vw, 7rem);
  line-height: 0.95;
  letter-spacing: -0.03em;
  font-weight: 700;

  em {
    color: ${({ theme }) => theme.colors.sky};
  }
`;

const Lead = styled(Reveal)`
  margin: 32px auto 0;
  max-width: 52ch;
  font-size: clamp(1.05rem, 1.5vw, 1.3rem);
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.onInk.text};
`;

const FormWrap = styled(Reveal)`
  max-width: 680px;
  margin: 48px auto 0;
  text-align: left;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Label = styled.label`
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 0.72rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.onInk.muted};
`;

const fieldStyles = `
  width: 100%;
  padding: 14px 16px;
  border-radius: 12px;
  font: inherit;
  transition: border-color 0.25s ease, background 0.25s ease;
`;

const Input = styled.input`
  ${fieldStyles};
  background: ${({ theme }) => theme.colors.onInk.field};
  border: 1px solid ${({ theme }) => theme.colors.onInk.border};
  color: ${({ theme }) => theme.colors.paper};

  &:focus-visible {
    outline: none;
    border-color: ${({ theme }) => theme.colors.sky};
    background: rgba(243, 241, 234, 0.1);
  }
`;

const Textarea = styled.textarea`
  ${fieldStyles};
  min-height: 160px;
  resize: vertical;
  background: ${({ theme }) => theme.colors.onInk.field};
  border: 1px solid ${({ theme }) => theme.colors.onInk.border};
  color: ${({ theme }) => theme.colors.paper};

  &:focus-visible {
    outline: none;
    border-color: ${({ theme }) => theme.colors.sky};
    background: rgba(243, 241, 234, 0.1);
  }
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-top: 4px;
`;

const Fallback = styled.p`
  font-size: 0.95rem;
  color: ${({ theme }) => theme.colors.onInk.muted};

  a {
    color: ${({ theme }) => theme.colors.paper};
    text-decoration: underline;
    text-underline-offset: 3px;

    &:hover {
      color: ${({ theme }) => theme.colors.sky};
    }
  }
`;

const Alert = styled.p<{ $tone: 'success' | 'error' }>`
  padding: 14px 18px;
  border-radius: 12px;
  border: 1px solid currentColor;
  font-size: 0.95rem;
  color: ${({ $tone, theme }) => ($tone === 'success' ? theme.colors.teal : theme.colors.error)};
`;

const Spinner = styled.span`
  display: inline-block;
  width: 1em;
  height: 1em;
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: 50%;
  animation: ${spin} 0.7s linear infinite;
`;

type Status = null | 'success' | 'error';

const EMPTY_FORM = { name: '', email: '', message: '' };

const Contact: React.FC = () => {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState<Status>(null);

  useEffect(() => {
    if (!status) return;

    const timer = window.setTimeout(() => setStatus(null), STATUS_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [status]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(formData)
      });

      if (!response.ok) throw new Error(`Formspree responded ${response.status}`);

      setFormData(EMPTY_FORM);
      setStatus('success');
      trackEvent('generate_lead', { method: 'contact_form' });
    } catch (error) {
      console.error('Form submission error:', error);
      setStatus('error');
      trackEvent('form_error', { method: 'contact_form' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Section id="contact">
      <Blob aria-hidden />
      <Inner>
        <Kicker>Get in touch</Kicker>
        <Title tag="h2" delay={80}>
          Have a project in <em>mind?</em>
        </Title>
        <Lead tag="p" delay={160}>
          Want to learn more about how we work? We'd love to hear from you — tell us what you're
          building.
        </Lead>

        <FormWrap delay={240}>
          <Form action={FORMSPREE_ENDPOINT} method="POST" onSubmit={handleSubmit}>
            {status === 'success' && (
              <Alert role="status" $tone="success">
                Thank you for your message. We'll be in touch shortly.
              </Alert>
            )}
            {status === 'error' && (
              <Alert role="alert" $tone="error">
                Something went wrong. Please try again, or email us directly.
              </Alert>
            )}

            <Row>
              <Field>
                <Label htmlFor="name">Name</Label>
                <Input
                  type="text"
                  id="name"
                  name="name"
                  autoComplete="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </Field>
              <Field>
                <Label htmlFor="email">Email</Label>
                <Input
                  type="email"
                  id="email"
                  name="email"
                  autoComplete="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </Field>
            </Row>

            <Field>
              <Label htmlFor="message">Message</Label>
              <Textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
              />
            </Field>

            <Actions>
              <PillButton type="submit" $variant="paper" $size="lg" disabled={isLoading}>
                {isLoading && <Spinner aria-hidden />}
                {isLoading ? 'Sending…' : 'Send message'}
              </PillButton>
              <Fallback>
                Prefer email?{' '}
                <a
                  href={`mailto:${siteConfig.email}`}
                  onClick={() => trackEvent('contact_email_click', { location: 'contact' })}
                >
                  {siteConfig.email}
                </a>
              </Fallback>
            </Actions>
          </Form>
        </FormWrap>
      </Inner>
    </Section>
  );
};

export default Contact;
